import { Component, Event, EventEmitter, Fragment, h, Prop, State, Watch } from '@stencil/core';
import { FormError, Response } from '../vs-standard-form/vs-standard-form.model';
import {
  defaultBaseUrl,
  defaultGenericErrorMessage,
  defaultSubmitButtonText,
  defaultVCESValidationEndPoint,
  getHeader, toExcludeAttribute,
} from '../vs-standard-form/vs-standard-form.constant';
import { disableSubmitButton, getFormFieldErrors, getHCaptchaPayload } from '../vs-standard-form/helpers/form.helper';
import { getUrlParameter, isDirRtl } from '@utils/utils';
import { handleErrorResponse } from '@src/workflows/vs-standard-form/helpers/error.helper';
import { v4 } from 'uuid';
import { parseDateValue } from '@src/workflows/vs-standard-form/helpers/date.helper';

@Component({
  tag: 'vs-vces-verification-form',
  shadow: false,
})
export class VsVcesVerificationForm {

  /**
   * @shortDescription If true, enable the developer mode.
   * @controlName dev
   */
  @Prop() dev: string;

  /**
   @shortDescription The fields to display.
   @controlName fields
   */
  @Prop() fields: string;
  /**
   @shortDescription If true, align the VCES verification component to the right.
   @controlName right
   */
  @Prop() right: boolean = isDirRtl();

  /**
   @shortDescription The heading of the VCES verification component.
   @controlName heading
   */
  @Prop() heading: string;

  /**
   @shortDescription The description of the VCES verification component.
   @controlName description
   */
  @Prop() description: string;

  /**
   @shortDescription The promo ID for the VCES verification component.
   @controlName promoId
   */
  @Prop() promoId: string;

  /**
   @shortDescription The base URL for the API endpoint.
   @controlName baseUrl
   */
  @Prop() baseUrl: string = defaultBaseUrl;

  /**
   @shortDescription The API endpoint.
   @controlName apiEndPoint
   */
  @Prop() apiEndPoint: string = defaultVCESValidationEndPoint;

  /**
   @shortDescription The generic error message to display.
   @controlName genericErrorMessage
   */
  @Prop() genericErrorMessage: string = defaultGenericErrorMessage;

  /**
   @shortDescription The text to display on the submit button.
   @controlName submitButtonText
   */
  @Prop() submitButtonText: string = defaultSubmitButtonText;

  /**
   @shortDescription The text to display on the reset button.
   @controlName resetButtonText
   */
  @Prop() resetButtonText: string;

  /**
   * @shortDescription CSS class for the grid
   * @controlName formGrid */
  @Prop() formGrid: string;

  /**
   @shortDescription required text label
   @controlName requiredText
   */
  @Prop() requiredText: string;

  /**
   @shortDescription The index of the VCES verification component (if there are multiple components on the same page).
   @controlName index
   */
  @Prop() index: string;

  /**
   @shortDescription The data center to use.
   @controlName xDataCenter
   */
  @Prop() xDataCenter = 'OCC0';

  /**
   @shortDescription Display label as an eyebrow view for an input field
   @controlName eyebrow
   */
  @Prop() eyebrow: boolean = false;

  /**
   * @shortDescription general config for the form to adopt two columns per row
   * @controlName isTwoColumnsPerRow */
  @Prop() isTwoColumnsPerRow = false;

  /**
   * @shortDescription JWT token for form submission
   * @controlName token */
  @Prop() token: string;

  /**
   * @shortDescription vcesVerificationErrorMessage
   * @controlName vcesVerificationErrorMessage */
  @Prop() vcesVerificationErrorMessage: string = 'Please enter a valid 16-digit code';

  // START GENAI@GHCOPILOT
  /**
   * @shortDescription repeatedCardErrorMessage
   * @controlName repeatedCardErrorMessage */
  @Prop() repeatedCardErrorMessage: string = 'This card cannot be added again as it has already been registered in the system.';
  // END GENAI@GHCOPILOT


  @Watch('token') tokenWatcher(newValue: any) {
    this.tokenInnerState = newValue;
  }

  /**
   * @shortDescription image url next to the form
   * @controlName sideImgUrl */
  @Prop() sideImgUrl: string;

  /**
   * @shortDescription image alt text next to the form
   * @controlName sideImgAltText */
  @Prop() sideImgAltText: string;
  /**
   * @shortDescription sitekey for hcaptcha
   * @controlName captchaSiteKey */
  @Prop() captchaSitekey!: string;

  /**
   * @shortDescription flag for maru workflow
   * @controlName isMaruWorkflow */
  @Prop() isMaruWorkflow: boolean = false;

  /**
   * @shortDescription flag for TBR workflow
   * @controlName isTbrWorkflow */
  @Prop() isTbrWorkflow: boolean = false;

  /**
   * @shortDescription flag for maru multi pan
   * @controlName isMaruMultiPAN */
  @Prop() isMaruMultiPan: boolean = false;

  /**
   * @shortDescription Array of PANs for the Maru multi-PAN workflow.
   * @controlName maruPans
   */
  @State() maruPans: string[] = [];

  /**
   * @shortDescription text for add card button
   * @controlName addCardButtonText */
  @Prop() addCardButtonText: string;

  /**
   * @shortDescription text for remove card button
   * @controlName removeCardButtonText
   */
  @Prop() removeCardButtonText: string = 'Remove Card';

  /**
   * @shortDescription text for add card instruction
   * @controlName addCardButtonInstruction
   */
  @Prop() addCardButtonInstruction: string = ''

  /**
   * @shortDescription text for submit instruction
   * @controlName submitButtonInstruction
   */
  @Prop() submitButtonInstruction: string = ''
  /**
   * * @shortDescription Determines if the form should be displayed in two columns
   * @controlName isTwoColumnForm */
  @Prop() isTwoColumnForm: boolean = false;

  /**
   * @shortDescription align to left
   * @controlName isAlignLeft */
  @Prop() isAlignLeft: boolean = false;

  @State() apiErrorMessages: string;
  @State() errorMessages: FormError[] = [];
  @State() errorCode: string;
  @State() tokenInnerState: string;


  /**
   * @shortDescription Emit VCES verified event on success
   * @controlName onVCESVerified */
  @Event({
    eventName: 'vCESVerified',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) vCESVerifiedHandler: EventEmitter<string>;

  /**
   * @shortDescription Emit VCES verified event on adding card
   * @controlName onMaruAddCardVerified */
  @Event({
    eventName: 'maruAddCardVerifiedEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) maruAddCardVerifiedHandler: EventEmitter<string>;

  /**
   * @shortDescription Emit reset form
   * @controlName resetFormEvent */
    // @ts-ignore
  @Event({
    eventName: 'resetVCESFormEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) resetVCESFormHandler: EventEmitter;

  /**
   * @shortDescription Emit form loading event
   * @controlName formLoadingEvent */
  @Event({
    eventName: 'vcesFormLoadingEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onVCESFormLoadingHandler: EventEmitter;

  componentWillLoad() {
    this.tokenInnerState = this.token;
  }

  private onVCESFormLoading = async (data) => {
    this.onVCESFormLoadingHandler.emit(data.detail);
  };

  private populateMaruPayload = (formData) => {
    const sessionIdName = 'RespondentSessionID';
    const respondentIdName = 'RespondentID';
    const rawData = formData?.detail || {};
    const toSubmitPayload = Object.keys(rawData)
      .filter(key => rawData[key]?.value?.length > 0 && key !== toExcludeAttribute && !this.maruPans.includes(rawData[key].value))
      .map(key => ({ key: key, value: parseDateValue(rawData[key].value) }));

    if (this.maruPans.length > 0) {
      let panCtr = 1;
      for (let pan of this.maruPans) {
        // START GENAI@GHCOPILOT
        if(panCtr==1){
          toSubmitPayload.push({
            key: `panToken`,
            value: pan
          })
        }else {
          toSubmitPayload.push({
            key: `panToken_${panCtr}`,
            value: pan
          })
        }
        // END GENAI@GHCOPILOT
        panCtr++;
      }
    }

    if (getUrlParameter(sessionIdName) && getUrlParameter(respondentIdName)) {

      let decodedSessionId = getUrlParameter(sessionIdName);
      let decodedRespondentId = getUrlParameter(respondentIdName);
      if (decodedSessionId.includes('+')) {
        console.log('plus sign found in session id', decodedSessionId);
        decodedSessionId = decodedSessionId.replace(/\+/g, '%2B');
      }
      /* START GENAI@GHCOPILOT */
      if (decodedRespondentId.includes('+')) {
        console.log('plus sign found in respondent id', decodedRespondentId);
        decodedRespondentId = decodedRespondentId.replace(/\+/g, '%2B');
      }
      /* END GENAI@GHCOPILOT */
     // START GENAI@GHCOPILOT
      toSubmitPayload.push({key:sessionIdName, value:decodedSessionId});
      toSubmitPayload.push({key:respondentIdName, value:decodedRespondentId});
      toSubmitPayload.push({key:'termsAndConditions1',value:['0']});
      toSubmitPayload.push({key:'personalEmail1', value: `${v4()}@visa.com`});
      // END GENAI@GHCOPILOT
    }
    return {
      promoId: this.promoId,
      form:[...toSubmitPayload, getHCaptchaPayload(formData)]
    };
  };

  private onMaruAddCard = async (formData) => {
    this.errorMessages = getFormFieldErrors(formData);
    const panValue = formData?.detail?.panToken?.value;
    if (this.errorMessages.length > 0 || !panValue) {
      this.onVCESFormLoadingHandler.emit(false);
      return;
    }
    if(this.maruPans.includes(panValue)){
      this.onVCESFormLoadingHandler.emit(false);
      disableSubmitButton(false);
      this.apiErrorMessages = this.repeatedCardErrorMessage;
      return;
    }
    try {
      const toSubmitPayload = {
        pan: panValue,
        hcaptcha: formData?.detail?.hcaptcha,
      };
      const urlEndPoint = this.baseUrl + '/mepa-api/app/v1/maru/validate';
      const response = await fetch(urlEndPoint, getHeader(this.xDataCenter, toSubmitPayload, this.tokenInnerState));
      const responseJson: Response = await response.json();
      if (!responseJson?.success) {
        this.handleError(responseJson);
      } else {
        this.maruPans=[...this.maruPans,panValue];
        this.apiErrorMessages='';
        this.maruAddCardVerifiedHandler.emit('success')
      }
    } catch (e) {
      this.handleError(e);
    }
    // START GENAI@GHCOPILOT
    this.onVCESFormLoadingHandler.emit(false);
    disableSubmitButton(false);
    // END GENAI@GHCOPILOT
  };

  private isDevMode = () => {
    return this.dev;
  }

  private onVCESFormReset = () => {
    this.resetVCESFormHandler.emit(true);
  }

  private onVCESFormSubmit = async (formData) => {
    if (this.isDevMode()) {
      this.vCESVerifiedHandler.emit("dev");
      return;
    }
    this.errorMessages = getFormFieldErrors(formData);
    let panValue:string
    if(this.isMaruWorkflow){
      panValue = formData?.detail?.panToken?.value;
    }else{
      panValue = formData?.detail?.pan?.value;
    }
    if (this.errorMessages.length > 0 || !panValue) {
      this.onVCESFormLoadingHandler.emit(false);
      return;
    }
    try {
      let toSubmitPayload = {};
      /* START GENAI@CHATGPT4 */
      if (this.isMaruWorkflow) {
        toSubmitPayload = this.populateMaruPayload(formData);
        let params = toSubmitPayload["form"].filter((item)=>item.key==="RespondentID" || item.key==="RespondentSessionID")
        if(params.length<2){
          this.apiErrorMessages="Unable to retrieve Session Id and Respondent Id";
          disableSubmitButton(false);
          this.onVCESFormLoadingHandler.emit(false);
          return;
        }
        /* END GENAI@CHATGPT4 */
      } else {
        toSubmitPayload = {
          pan: panValue,
          hcaptcha: formData?.detail?.hcaptcha,
        };
      }
      const urlEndPoint = this.baseUrl + this.apiEndPoint;
      const response = await fetch(urlEndPoint, getHeader(this.xDataCenter, toSubmitPayload, this.tokenInnerState));
      const responseJson: Response = await response.json();
      if (!responseJson?.success) {
        this.handleError(responseJson);
      } else {
        if(this.isMaruWorkflow){
          // START GENAI@GHCOPILOT
         this.vCESVerifiedHandler.emit(responseJson?.successMessage);
          // END GENAI@GHCOPILOT
        } else if (this.isTbrWorkflow) {
          this.vCESVerifiedHandler.emit(responseJson?.vcardId);
        }
        else {
          this.vCESVerifiedHandler.emit(panValue);
        }
      }
    } catch (e) {
      this.handleError(e);
    }
    // START GENAI@GHCOPILOT
    disableSubmitButton(false);
    // END GENAI@GHCOPILOT
  };

  private handleError = (response: Response) => {
    this.onVCESFormLoadingHandler.emit(false);
   // START GENAI@GHCOPILOT
    if(!this.isMaruWorkflow) {
      this.resetVCESFormHandler.emit(response);
    }
    // END GENAI@GHCOPILOT
    this.resetState();
    if (!response) {
      return;
    }
    const errorResponse = handleErrorResponse(response, this.genericErrorMessage);
    this.apiErrorMessages = errorResponse.apiErrorMessages;
    this.errorCode = errorResponse.errorCode;
    if(this.isMaruWorkflow){
      // @ts-ignore
      if(response.code==="SCS_INVALID_CARD"){
        this.apiErrorMessages = this.vcesVerificationErrorMessage
      }
    }
  };

  private reloadCaptcha = () => {
    const hCaptcha = document.querySelector('vs-hcaptcha');
    hCaptcha?.resetCaptcha();
  };

  private onMaruRemoveCard = (event) => {
    /* START GENAI@CHATGPT4 */
    const itmIdx = this.maruPans.indexOf(event.detail);
    if (itmIdx > -1) {
      this.maruPans = this.maruPans.filter((_, idx) => idx !== itmIdx);
    }
    /* END GENAI@CHATGPT4 */
  }
  private resetState = () => {
    this.apiErrorMessages = '';
    this.reloadCaptcha();
  };

  render() {
    return (
      <Fragment>
        <vs-form-builder
          id={`vces-verification-form-${this.index}`}
          isAlignLeft={this.isAlignLeft}
          formGrid={this.formGrid}
          requiredText={this.requiredText}
          index={this.index}
          fields={this.fields}
          eyebrow={this.eyebrow}
          heading={this.heading}
          description={this.description}
          isTwoColumnForm={this.isTwoColumnForm}
          isTwoColumnsPerRow={this.isTwoColumnsPerRow}
          sideImgUrl={this.sideImgUrl}
          sideImgAltText={this.sideImgAltText}
          submitButtonText={this.submitButtonText}
          resetButtonText={this.resetButtonText}
          isMaruMultiPan={this.isMaruMultiPan}
          isMaruWorkflow={this.isMaruWorkflow}
          maruCards={this.maruPans}
          onAddCardEvent={this.onMaruAddCard}
          onRemoveCardEvent={this.onMaruRemoveCard}
          addCardButtonText={this.addCardButtonText}
          removeCardButtonText={this.removeCardButtonText}
          addCardButtonInstruction={this.addCardButtonInstruction}
          submitButtonInstruction={this.submitButtonInstruction}
          onFormLoadingEvent={this.onVCESFormLoading}
          onFormSubmitEvent={this.onVCESFormSubmit}
          onResetFormEvent={this.onVCESFormReset}
          captchaSiteKey={this.captchaSitekey}>
          <div slot='errorSlot'>
            {
              this.errorMessages && this.errorMessages.length > 0 &&
              <p role='alert'
                 class='vs-col-12 vs-mb-2 vs-text vs-meta'
                 aria-live='polite' id='errorFormMessage'>
                <ul class='vs-error'>
                  {this.errorMessages.map(error => {
                    return (<li><a style={{ color: 'rgb(201, 0, 0)' }}
                                   href={`#${error.formIndex}`}>{error.message}</a></li>);
                  })}
                </ul>
              </p>
            }
          </div>
          <div slot='headingSlot'>
            <slot name='headingSlot' />
          </div>
          <div slot='descriptionSlot'>
            <slot name='descriptionSlot' />
          </div>
          <div slot='additionalContentSlot'>
            <slot name='additionalContentSlot' />
          </div>
          <div slot='footerSlot'>
            <slot name='footerSlot' />
          </div>
          <div slot='inputSlot'>
            <slot name='inputSlot' />
          </div>
          <div slot='apiErrorSlot'>
            {
              this.apiErrorMessages &&
              <p role='alert' aria-live='polite'
                 class='vs-meta vs-text vs-alert vs-inline-alert vs-pb-1'
                 id='genericErrorMessage'>
                <span aria-hidden='true' class='visa-icon-alert' />
                <span innerHTML={this.apiErrorMessages} />
              </p>
            }
          </div>
        </vs-form-builder>
      </Fragment>
    );
  }
}

